import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { AlertCircle, CheckCircle, Eye, Trash2, X, Image as ImageIcon, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { formatDateTimeDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';

const CriticalAlerts = () => {
  const { isMobile, isTablet } = useDeviceDetection();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => {
    fetchAlerts();
  }, [filterStatus]);

  const fetchAlerts = async () => {
    try {
      const url = filterStatus ? `${API}/battery-mismatch-alerts?status=${filterStatus}` : `${API}/battery-mismatch-alerts`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAlert = (alert) => {
    setSelectedAlert(alert);
    setViewDialogOpen(true);
  };

  const handleResolveClick = (alert) => {
    setSelectedAlert(alert);
    setResolveDialogOpen(true);
  };

  const handleResolveSubmit = async () => {
    if (!resolutionNotes) {
      toast.error('Please provide resolution notes');
      return;
    }

    try {
      await axios.put(
        `${API}/battery-mismatch-alerts/${selectedAlert.id}/resolve`,
        null,
        {
          params: { resolution_notes: resolutionNotes },
          headers: getAuthHeader()
        }
      );
      
      toast.success('✅ Alert resolved successfully');
      setResolveDialogOpen(false);
      setResolutionNotes('');
      setSelectedAlert(null);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    try {
      await axios.delete(`${API}/battery-mismatch-alerts/${alertId}`, { headers: getAuthHeader() });
      toast.success('Alert deleted successfully');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-orange-100 text-orange-800',
      resolved: 'bg-green-100 text-green-800',
      dismissed: 'bg-slate-100 text-slate-800'
    };
    return variants[status] || variants.pending;
  };

  const formatDate = (dateString) => {
    return formatDateTimeDisplay(dateString);
  };

  const pendingCount = alerts.filter(a => a.status === 'pending').length;

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading alerts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Critical Alerts</h1>
          <p className="text-slate-600 mt-1">Battery mismatch incidents requiring review</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-red-500 text-white text-lg px-4 py-2">
            {pendingCount} Pending
          </Badge>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={filterStatus === 'pending' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('pending')}
        >
          Pending ({alerts.filter(a => a.status === 'pending').length})
        </Button>
        <Button
          variant={filterStatus === 'resolved' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('resolved')}
        >
          Resolved ({alerts.filter(a => a.status === 'resolved').length})
        </Button>
        <Button
          variant={filterStatus === '' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('')}
        >
          All ({alerts.length})
        </Button>
      </div>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">No Alerts</h3>
            <p className="text-slate-600">
              {filterStatus === 'pending' ? 'No pending alerts at the moment!' : 'No alerts to display.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {alerts.map(alert => (
            <Card key={alert.id} className={alert.status === 'pending' ? 'border-orange-300 bg-orange-50' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      <CardTitle className="text-lg">Battery Mismatch - {alert.rider_name}</CardTitle>
                      <Badge className={getStatusBadge(alert.status)}>
                        {alert.status.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <div><strong>Bike:</strong> {alert.bike_number}</div>
                      <div><strong>Emirates ID:</strong> {alert.emirates_id}</div>
                      <div><strong>Date:</strong> {formatDate(alert.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleViewAlert(alert)}>
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {alert.status === 'pending' && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleResolveClick(alert)}>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Resolve
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(alert.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Alert className="bg-orange-100 border-orange-300">
                    <AlertDescription>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <div className="font-semibold text-orange-800 mb-1">Expected Batteries:</div>
                          {alert.expected_batteries.map((serial, i) => (
                            <div key={i} className="font-mono text-sm">• {serial}</div>
                          ))}
                        </div>
                        <div>
                          <div className="font-semibold text-red-800 mb-1">Actual Batteries:</div>
                          {alert.actual_batteries.map((serial, i) => (
                            <div key={i} className="font-mono text-sm text-red-600">• {serial}</div>
                          ))}
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>

                  <div>
                    <div className="font-semibold text-sm mb-1">Description:</div>
                    <div className="text-sm text-slate-700 bg-white p-3 rounded border">
                      {alert.description}
                    </div>
                  </div>

                  {alert.photos && alert.photos.length > 0 && (
                    <div>
                      <div className="font-semibold text-sm mb-2">Photos ({alert.photos.length}):</div>
                      <div className="grid grid-cols-5 gap-2">
                        {alert.photos.map((photo, index) => (
                          <div
                            key={index}
                            className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-75"
                            onClick={() => {
                              setSelectedImage(photo);
                              setImageDialogOpen(true);
                            }}
                          >
                            <img
                              src={photo}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {alert.status === 'resolved' && alert.resolution_notes && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="font-semibold text-sm text-green-800 mb-1">Resolution:</div>
                      <div className="text-sm text-green-700">{alert.resolution_notes}</div>
                      <div className="text-xs text-green-600 mt-1">
                        Resolved by {alert.resolved_by} on {formatDate(alert.resolved_at)}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Alert Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Alert Details</DialogTitle>
          </DialogHeader>
          {selectedAlert && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Rider Name</Label>
                  <div className="font-semibold">{selectedAlert.rider_name}</div>
                </div>
                <div>
                  <Label>Bike Number</Label>
                  <div className="font-semibold">{selectedAlert.bike_number}</div>
                </div>
                <div>
                  <Label>Emirates ID</Label>
                  <div className="font-mono">{selectedAlert.emirates_id}</div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className={getStatusBadge(selectedAlert.status)}>
                    {selectedAlert.status.toUpperCase()}
                  </Badge>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <div className="bg-slate-50 p-3 rounded mt-1">{selectedAlert.description}</div>
              </div>

              {selectedAlert.photos && selectedAlert.photos.length > 0 && (
                <div>
                  <Label>Photos</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {selectedAlert.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Photo ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-75"
                        onClick={() => {
                          setSelectedImage(photo);
                          setImageDialogOpen(true);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Alert</DialogTitle>
            <DialogDescription>
              Provide resolution notes to close this alert
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Resolution Notes *</Label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe how this issue was resolved..."
                rows={4}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleResolveSubmit}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Resolved
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image View Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'}`}>
          <DialogHeader>
            <DialogTitle>Photo View</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CriticalAlerts;
